import React from "react"
import { useDispatch } from "react-redux"

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material"

import { masterSchemaSlice, useColumnSchemas, useTableSchema } from "@/slice/masterSchemaSlice"
import { gqlClient } from "@/util/graphql"

export const ColumnSettingModal = (props: {
	tableName: string
	columnName: string
	isOpen: boolean
	onClose: (result: boolean) => void
}) => {
	const { tableName, columnName, isOpen, onClose } = props

	const dispatch = useDispatch()

	const columnSchema = useColumnSchemas(tableName)[columnName] ?? {}
	const referenceableColumns = useTableSchema(columnSchema.ref ?? undefined)?.columns

	const [displayName, setDisplayName] = React.useState("")
	React.useEffect(() => setDisplayName(columnSchema.displayName ?? ""), [columnSchema.displayName])
	const [refDisplay, setRefDisplay] = React.useState<string | undefined>(columnSchema.refDisplay ?? undefined)
	React.useEffect(() => {
		if (columnSchema.refDisplay !== undefined && columnSchema.refDisplay !== null) {
			setRefDisplay(columnSchema.refDisplay)
		}
	}, [columnSchema.refDisplay])
	const [description, setDescription] = React.useState("")
	React.useEffect(() => setDescription(columnSchema.description ?? ""), [columnSchema.description])

	const clickSaveButon = React.useCallback(() => {
		gqlClient()
			.updateMasterSchema({
				tableName,
				columns: { name: columnName, refDisplay, displayName, description },
			})
			.then(({ updateMasterSchema: { result } }) => {
				if (result) {
					dispatch(masterSchemaSlice.actions.setDisplayName({ tableName, columnName, displayName }))
					if (refDisplay !== undefined) {
						dispatch(masterSchemaSlice.actions.setRefDisplay({ tableName, columnName, refDisplay }))
					}
					dispatch(masterSchemaSlice.actions.setDescription({ tableName, columnName, description }))
					onClose(true)
					// モーダルが閉じられるときにrefDisplayの状態を初期化する
					setRefDisplay(undefined)
				}
			})
	}, [columnName, description, dispatch, displayName, onClose, refDisplay, tableName])

	const onChangeDisplayName = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setDisplayName(e.target.value)
	}, [])
	const onChangeDescription = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setDescription(e.target.value)
	}, [])
	const onChangeRefDisplay = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setRefDisplay(e.target.value)
	}, [])

	if (columnSchema === undefined) return <></>
	return (
		<Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
			<DialogTitle>列の情報と設定</DialogTitle>
			<DialogContent>
				<Grid spacing={4} rowSpacing={1} container>
					<Grid item>
						<Stack direction="row" spacing={2} alignItems="center">
							<Typography color="grey.600" fontSize={12}>
								列の名前:
							</Typography>
							<Typography color="grey.900" fontWeight="medium">
								{columnSchema.name}
							</Typography>
						</Stack>
					</Grid>

					<Grid item>
						<Stack direction="row" spacing={2} alignItems="center">
							<Typography color="grey.600" fontSize={12}>
								列のタイプ:
							</Typography>
							<Typography color="grey.900" fontWeight="medium">
								{columnSchema.type}
							</Typography>
						</Stack>
					</Grid>

					<Grid item>
						<Stack direction="row" spacing={2} alignItems="center">
							<Typography color="grey.600" fontSize={12}>
								必須項目:
							</Typography>
							<Typography color="grey.900" fontWeight="medium">
								{columnSchema.required ? "はい" : "いいえ"}
							</Typography>
						</Stack>
					</Grid>

					<Grid item>
						<Stack direction="row" spacing={2} alignItems="center">
							<Typography color="grey.600" fontSize={12}>
								他のテーブルを参照する:
							</Typography>
							<Typography color="grey.900" fontWeight="medium">
								{columnSchema.ref ? "はい" : "いいえ"}
							</Typography>
						</Stack>
					</Grid>

					{columnSchema.ref && (
						<Grid item>
							<Stack direction="row" spacing={2} alignItems="center">
								<Typography color="grey.600" fontSize={12}>
									参照するテーブル名:
								</Typography>
								<Typography color="grey.900" fontWeight="medium">
									{columnSchema.ref}
								</Typography>
							</Stack>
						</Grid>
					)}
				</Grid>

				<Stack mt={2} mb={1} spacing={4}>
					<TextField
						margin="dense"
						label="ヘッダーの表示名"
						type="text"
						fullWidth
						variant="standard"
						value={displayName}
						placeholder={columnSchema.name}
						onChange={onChangeDisplayName}
					/>

					<TextField
						margin="dense"
						label="列の説明"
						type="text"
						fullWidth
						variant="standard"
						value={description}
						onChange={onChangeDescription}
					/>

					{columnSchema.ref && referenceableColumns && (
						<TextField
							margin="dense"
							label="選択項目に表示する参照テーブルの列名"
							fullWidth
							variant="standard"
							select
							value={refDisplay}
							onChange={onChangeRefDisplay}
						>
							{referenceableColumns.map((refColumn) => (
								<MenuItem key={refColumn.name} value={refColumn.name}>
									{refColumn.name}
								</MenuItem>
							))}
						</TextField>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => props.onClose(false)}>キャンセル</Button>
				<Button onClick={clickSaveButon}>設定を保存する</Button>
			</DialogActions>
		</Dialog>
	)
}
